import React from 'react';
import { FolderOpen, Star, FileText, ChevronRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

/**
 * Collection card component displaying a curated file group
 */
const CollectionCard = ({
    collection,
    onClick,
    onEdit,
    onDelete,
    isAdmin = false
}) => {
    // Dynamically get the icon component
    const getIconComponent = (iconName) => {
        const formattedName = iconName
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
        return LucideIcons[formattedName] || FolderOpen;
    };

    const IconComponent = getIconComponent(collection.icon || 'folder-open');

    return (
        <div
            onClick={() => onClick?.(collection)}
            className="group relative bg-white border border-gray-100 rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-gray-100/50 hover:-translate-y-0.5 hover:border-gray-200"
        >
            {/* Colored Header */}
            <div
                className="h-20 relative overflow-hidden"
                style={{ backgroundColor: collection.color || '#6366f1' }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <IconComponent size={40} className="text-white/90" />
                </div>

                {/* Featured Badge */}
                {collection.is_featured && (
                    <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-lg flex items-center gap-1">
                        <Star size={12} className="text-yellow-300 fill-yellow-300" />
                        <span className="text-xs text-white font-medium">Featured</span>
                    </div>
                )}

                {/* Phase Badge */}
                {collection.phase && (
                    <div className="absolute top-2 left-2 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-lg">
                        <span className="text-xs text-white font-medium capitalize">{collection.phase}</span>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-1 group-hover:text-primary transition-colors">
                    {collection.name}
                </h3>

                {collection.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                        {collection.description}
                    </p>
                )}

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-gray-400">
                        <FileText size={14} />
                        <span className="text-sm">
                            {collection.file_count || 0} file{collection.file_count !== 1 ? 's' : ''}
                        </span>
                    </div>

                    <div className="flex items-center gap-1 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-sm font-medium">View</span>
                        <ChevronRight size={16} />
                    </div>
                </div>
            </div>

            {/* Admin Actions */}
            {isAdmin && (onEdit || onDelete) && (
                <div
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1"
                    onClick={(e) => e.stopPropagation()}
                >
                    {onEdit && (
                        <button
                            onClick={() => onEdit(collection)}
                            className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-sm transition-colors"
                            title="Edit Collection"
                        >
                            <LucideIcons.Pencil size={14} className="text-gray-600" />
                        </button>
                    )}
                    {onDelete && (
                        <button
                            onClick={() => onDelete(collection)}
                            className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-sm transition-colors"
                            title="Delete Collection"
                        >
                            <LucideIcons.Trash2 size={14} className="text-red-500" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default CollectionCard;
